import Swal from "sweetalert2";
import { localTranslate } from "./translation";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import sanitize from "./sanitize";
dayjs.extend(customParseFormat);
dayjs.extend(utc);

function navHeight() {
  const rootElement = document.documentElement;
  const computedStyles = getComputedStyle(rootElement);
  let navHeight = computedStyles.getPropertyValue('--nav-height');
  if (navHeight) return parseInt(navHeight.replace('px', ''));
  return null;
}

function scrollInto(query, intoView = false, offset = 0) {
  const el = document.querySelector(query);
  if (el) {
    if (intoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    let y = el.getBoundingClientRect().top + (window.scrollY - (navHeight() ?? 0)) + offset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }
}

function scrollToTop(el = window) {
  el.scrollTo({ top: 0, behavior: 'smooth' });
}

function errorMessage(err, context, doSlice = true) {
  console.log(
    'errorMessage',
    JSON.stringify(
      { err, context },
      (k, v) => (k === 'X-XSRF-TOKEN' ? '[REDACTED]' : v)
    )
  );

  const fallbackMessage = 'An unexpected error occurred.';
  const fallbackKey = 'global.unexpected_error';

  let msg = err?.message;
  if (!msg && typeof err === 'string') msg = err;

  let translationKey = null;
  let reqId = null;
  let statusCode = null;

  if (err?.response) {
    const data = err.response?.data || {};
    translationKey = data.translation_key || null;
    msg = data.message || msg;

    reqId = err.response?.headers?.['x-request-id'] || null;
    statusCode = err.response?.status || null;

    if (statusCode === 503) {
      msg = 'Die Anwendung befindet sich derzeit im Wartungsmodus. Bitte versuchen Sie es später erneut.';
      translationKey = 'global.maintenance_mode';
    } else if (statusCode >= 500) {
      msg = 'Entschuldigung, auf dem Server ist ein Fehler aufgetreten.';
      translationKey = data?.translation_key || 'global.server_error';
    } else {
      msg = (msg ? (doSlice ? msg?.toString().slice(0, 100) : msg.toString()) : fallbackMessage);
    }
  } else {
    translationKey = err?.translation_key || null;
    msg = (msg ? (doSlice ? msg?.toString().slice(0, 100) : msg.toString()) : fallbackMessage);
  }

  return {
    message: msg ?? fallbackMessage,
    translationKey,
    reqId,
    context: context ?? null,
    statusCode,
  };
}

function fileDownload(data, filename, mime, bom) {
  var blobData = (typeof bom !== 'undefined') ? [bom, data] : [data]
  var blob = new Blob(blobData, { type: mime || 'application/octet-stream' });
  if (typeof window.navigator.msSaveBlob !== 'undefined') {
    // IE workaround for "HTML7007: One or more blob URLs were
    // revoked by closing the blob for which they were created.
    // These URLs will no longer resolve as the data backing
    // the URL has been freed."
    window.navigator.msSaveBlob(blob, filename);
  }
  else {
    var blobURL = (window.URL && window.URL.createObjectURL) ? window.URL.createObjectURL(blob) : window.webkitURL.createObjectURL(blob);
    var tempLink = document.createElement('a');
    tempLink.style.display = 'none';
    tempLink.href = blobURL;
    tempLink.setAttribute('download', filename);

    // Safari thinks _blank anchor are pop ups. We only want to set _blank
    // target if the browser does not support the HTML5 download attribute.
    // This allows you to download files in desktop safari if pop up blocking
    // is enabled.
    if (typeof tempLink.download === 'undefined') {
      tempLink.setAttribute('target', '_blank');
    }

    document.body.appendChild(tempLink);
    tempLink.click();

    // Fixes "webkit blob resource error 1"
    setTimeout(function () {
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(blobURL);
    }, 200)
  }
}

function getFilenameFromDisposition(disposition, fallback = 'download') {
  if (!disposition || typeof disposition !== 'string') return fallback;

  const utf8Match = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]).replace(/["']/g, '') || fallback;
    } catch (e) {
      // fall through to plain filename parsing
    }
  }

  const plainMatch = disposition.match(/filename\s*=\s*"?([^";]+)"?/i);
  if (plainMatch?.[1]) {
    return plainMatch[1].trim() || fallback;
  }

  return fallback;
}

function fileDownloadResponse(response, fallbackFilename = 'download', mime, bom) {
  const disposition = response?.headers?.['content-disposition'];
  const filename = getFilenameFromDisposition(disposition, fallbackFilename);
  const blobMime = mime || response?.data?.type || response?.headers?.['content-type'] || 'application/octet-stream';

  fileDownload(response?.data, filename, blobMime, bom);
}

function empty(x) {
  return x === null || x === undefined || (typeof x === 'string' && x.trim() === '');
}

function getTextFromHtml(html) {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
}

function emptyHtml(x) {
  return empty(x) || x === '<p><br></p>' || !getTextFromHtml(sanitize(x));
}

function isTrue(val) {
  if (typeof val === "boolean") return val;
  if (typeof val === "number") return val === 1;
  if (typeof val === "string") return ["1", "true"].includes(val.toLowerCase());
  return false;
}

function isFalse(val) {
  if (typeof val === "boolean") return val === false;
  if (typeof val === "number") return val === 0;
  if (typeof val === "string") return ["0", "false"].includes(val.toLowerCase());
  return false;
}

function nl2br(text) {
  return String(text)
    .replace(/\\n/g, '\n')
    .replace(/\n/g, '<br />');
}

function removeTrailingZeros(str, decimal = ',') {
  if (str) str = String(str);

  if (str && str.includes(decimal)) {
    return str.replace(/0+$/, '').replace(decimal === '.' ? /\.$/ : /,$/, '');
  }
  return str;
}

function formatFixedDigits(x, digits = 0, fillUp = false) {
  digits = +digits; // to number in case of string

  const raw = String(x ?? '');
  const separator = raw.includes(',') ? ',' : raw.includes('.') ? '.' : ',';

  if (typeof x === 'string' && x.includes(',')) x = formatNumberTo(x, '.');

  x = +x; // to number in case of string

  let newX = x.toFixed(digits);
  if (separator === ',') {
    newX = newX.replace('.', ',');
  }

  if (!fillUp) {
    newX = removeTrailingZeros(newX, separator);
  }

  return newX;
}

function formatMoney(x, decimal = true) {
  const opt = {};
  if (decimal) {
    opt.minimumFractionDigits = 2;
    opt.maximumFractionDigits = 2;
  } else {
    opt.minimumFractionDigits = 0;
    opt.maximumFractionDigits = 0;
  }
  if (typeof x === 'string' && x.includes(',')) x = formatNumberTo(x, '.');

  return (Number(x).toLocaleString('de-DE', opt));
}

function formatThousands(value, thousandSeparator = '.', decimalSeparator = ',') {
  if (value === null || value === undefined) return '';

  let str = String(value).trim();
  if (!str) return '';

  let integerPart = str;
  let decimalPart = '';

  if (str.includes(decimalSeparator) && decimalSeparator) {
    const parts = str.split(decimalSeparator);
    integerPart = parts.slice(0, -1).join('');
    if (parts.length > 1) {
      decimalPart = parts[parts.length - 1];
    }
  }

  integerPart = integerPart.replace(/[.,\s]/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);

  if (decimalPart) {
    return integerPart + decimalSeparator + decimalPart;
  }

  return integerPart;
}

function formatNumberTo(x, decimal = ',') {
  if (decimal === ',') {
    if (x?.toString().includes(',')) return x; // already comma-decimal
    if (!x?.toString().includes('.')) return x;

    return x?.toString().replace(/,/g, '').replace('.', ',');
  } else if (decimal === '.') {
    if (!x?.toString().includes(',')) return x;

    return x?.toString().replace(/\./g, '').replace(',', '.');
  }
  return x;
}

function formatInputNumber(val, decimals = 2) {
  if (decimals < 1) return val.replace(/[^0-9]/g, '');

  const value = val.replace(/[^-0-9,]/g, '');

  const parts = value.split(',');
  if (parts.length > 1) {
    parts[1] = parts[1].slice(0, decimals);
  }

  return parts[0] + (parts[1] ? ',' + parts[1] : '');
}

function fieldDecimalCheck(val, field) {
  return (val.toString()).match(!field || field.decimal == 0 ? /^\d+$/ : new RegExp(`^\\d*(,{1}(\\d{1,${field.decimal}}))?$`));
}

function formatDateTime(val, forceDateOnly = false, toLocal = true, withSeconds = false) {
  if (!val) return '';

  const isDateOnly = (!val.includes(':') && !val.includes('T'));
  const ins = dayjs(val);
  const hasExplicitTimezone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(val);
  const format = forceDateOnly || isDateOnly ? 'DD.MM.YYYY' : ('DD.MM.YYYY HH:mm' + (withSeconds ? ':ss' : ''));

  if (!toLocal) {
    return ins.format(format);
  }

  // For date-only values, keep the local calendar date as-is.
  if (isDateOnly) {
    return ins.format('DD.MM.YYYY');
  }

  // If input already has timezone info (Z or ±HH:mm), avoid double conversion.
  if (hasExplicitTimezone) {
    return ins.local().format(format);
  }

  // For naive timestamps (no timezone), treat as UTC then convert to local.
  return ins.utc(true).local().format(format);
}

function formatTime(val, toLocal = true, withSeconds = false) {
  if (!val) return '';

  const isDateOnly = (!val.includes(':') && !val.includes('T'));
  const hasExplicitTimezone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(val);

  if (isDateOnly) return formatDateTime(val, true, toLocal);

  const ins = dayjs(val);
  const format = 'HH:mm' + (withSeconds ? ':ss' : '');

  if (!toLocal) {
    return ins.format(format);
  }

  if (hasExplicitTimezone) {
    return ins.local().format(format);
  }

  return ins.utc(true).local().format(format);
}

function formatRequest(data) {
  if (Array.isArray(data)) {
    for (let i = 0; i < data.length; i++) {
      data[i] = formatRequest(data[i]);
    }
  } else if (typeof data === "object" && data !== null) {
    for (const item in data) {
      data[item] = formatRequest(data[item]);
    }
  } else if (data && typeof data !== 'boolean' && data !== null && (typeof data === 'string' && !data.match(/^\d{2}\.\d{2}\.\d{4}$/))) {
    const formatted = formatNumberTo(data, '.');
    if (!isNaN(data) || !isNaN(formatted)) return formatted;
  }

  return data;
}

function formatResponse(data) {
  if (Array.isArray(data)) {
    for (let i = 0; i < data.length; i++) {
      data[i] = formatResponse(data[i]);
    }
  } else if (typeof data === "object" && data !== null) {
    for (const item in data) {
      data[item] = formatResponse(data[item]);
    }
  } else if (data && typeof data !== 'boolean' && data !== null) {
    if (!isNaN(data)) return formatNumberTo(data);
  }

  return data;
}

async function leaveConfirm() {
  const mySwal = Swal.mixin({
    customClass: {
      confirmButton: "btn btn-primary mr-2",
      cancelButton: "btn btn-outline-primary"
    },
    buttonsStyling: false
  });

  const { isConfirmed } = await mySwal.fire({
    text: localTranslate('global.leave_confirm_message', 'Sie haben ungespeicherte Änderungen. Möchten Sie die Seite wirklich verlassen?'),
    showCancelButton: true,
    confirmButtonText: localTranslate('global.leave_confirm_yes', 'Ja'),
    cancelButtonText: localTranslate('global.leave_confirm_no', 'Nein, abbrechen')
  });

  return isConfirmed;
}

// Shown when the evaluator overview goes idle with unsaved changes. Closes on its own after `timer`,
// so the scorecard lock is released before the backend lock (15 minutes) expires.
async function idleCloseConfirm(timer = 10 * 60 * 1000) {
  const mySwal = Swal.mixin({
    customClass: {
      confirmButton: "btn btn-primary mr-2",
      cancelButton: "btn btn-outline-primary"
    },
    buttonsStyling: false
  });

  const { isConfirmed, dismiss } = await mySwal.fire({
    text: localTranslate('global.idle_close_confirm_message', 'Sie waren 5 Minuten inaktiv. Möchten Sie die Seite schließen und nicht gespeicherte Änderungen verwerfen? Wenn keine Antwort erfolgt, wird die Seite in 10 Minuten automatisch geschlossen.'),
    showCancelButton: true,
    confirmButtonText: localTranslate('global.leave_confirm_yes', 'Ja'),
    cancelButtonText: localTranslate('global.leave_confirm_no', 'Nein, abbrechen'),
    timer
  });

  if (dismiss === Swal.DismissReason.timer) return 'timeout';
  if (isConfirmed) return 'close';
  return 'continue';
}

// Same look as the logout modal in `Layout/User.js`.
function idleClosedInfo(discarded = false) {
  return Swal.fire({
    text: discarded
      ? localTranslate('global.idle_closed_discarded_message', 'Die Seite wurde wegen Inaktivität geschlossen. Nicht gespeicherte Änderungen wurden verworfen.')
      : localTranslate('global.idle_closed_message', 'Die Seite wurde wegen Inaktivität geschlossen.'),
    confirmButtonText: localTranslate('button.ok', 'OK'),
    allowOutsideClick: false,
    customClass: {
      confirmButton: 'btn btn-primary'
    },
    buttonsStyling: false
  });
}

export { navHeight, scrollInto, scrollToTop, errorMessage, fileDownload, fileDownloadResponse, empty, emptyHtml, isTrue, isFalse, getTextFromHtml, nl2br, removeTrailingZeros, formatFixedDigits, formatMoney, formatThousands, formatNumberTo, formatInputNumber, fieldDecimalCheck, formatDateTime, formatTime, formatRequest, formatResponse, leaveConfirm, idleCloseConfirm, idleClosedInfo };
